import { IQuestionModel } from '@care-portals/api-interfaces';
import { caProvinces, usStates } from '../../data';

const questionsModels: IQuestionModel[] = [
  {
    label: 'In Network Care Coordinator Intake Form',
    educationalSlide: `
      Here at Momwell, we understand your time is precious. Please complete our online history form before your first appointment to ensure a smooth initial session. It will take <b> 10-20 minutes </b> to complete, but here’s the good news:
      <br />
      <ul>
      <li>You only need to complete this form once.</li>
      <li>Your information is completely confidential.</li>
      <li>The form saves automatically, so you can complete it at your own pace, in one sitting or multiple sessions.</li>
      </ul>
      `,
    field: 'momwellIntakeNetworkIntro',
    type: 'info',
    answer: {}
  },
  {
    label: 'Intake Information',
    field: 'momwellIntakeNetworkIntakeInformation',
    type: 'info',
    answer: {
      type: 'list',
      items: [
        {
          label: 'First Name',
          type: 'text',
          placeholder: 'Please enter your first name',
          name: 'firstName',
          required: true,
          halfWidth: true
        },
        {
          label: 'Last Name',
          type: 'text',
          placeholder: 'Please enter your last name',
          name: 'lastName',
          required: true,
          halfWidth: true
        },
        {
          label: 'Email',
          type: 'text',
          placeholder: 'Please enter your email',
          name: 'email',
          required: true,
          halfWidth: true
        },
        {
          label: 'Client Location:',
          type: 'single_select',
          level: 'child',
          field: 'clientLocation',
          halfWidth: false,
          options: [...usStates, ...caProvinces].map(s => ({
            key: 'clientLocation_' + s.code,
            value: s.province
          }))
        },
        {
          label: 'How did you hear about Momwell?',
          type: 'single_select',
          level: 'child',
          field: 'howDidYouHearAboutUs',
          halfWidth: false,
          options: [
            {
              key: 'howDidYouHearAboutUs_childinternetSearch',
              value: 'Internet Search'
            },
            {
              key: 'howDidYouHearAboutUs_friendOrFamily',
              value: 'Friend or Family'
            },
            {
              key: 'howDidYouHearAboutUs_doctorOrHealthcareProvider',
              value: 'Doctor or Healthcare Provider'
            },
            {
              key: 'howDidYouHearAboutUs_advertisement',
              value: 'Advertisement'
            },
            {
              key: 'howDidYouHearAboutUs_socialMedia',
              value: 'Social Media'
            },
            {
              key: 'howDidYouHearAboutUs_podcast',
              value: 'Podcast'
            },
            {
              key: 'howDidYouHearAboutUs_brandPartner',
              value: 'Brand Partner (Willow, Huckleberry, etc)'
            },
            {
              key: 'howDidYouHearAboutUs_other',
              value: 'Other (Please specify)',
              answer: {
                type: 'text',
                rows: 3,
                name: 'howDidYouHearAboutUsOtherDesc',
                placeholder: ''
              }
            }
          ]
        },
        {
          label: 'Reason for booking?',
          type: 'text',
          placeholder: '',
          rows: 4,
          name: 'reasonForBooking',
          required: true,
          halfWidth: false
        }
      ]
    }
  },
  {
    label: 'Basic Risk Assessment',
    field: 'momwellIntakeNetworkBasicRiskAssessment',
    type: 'info',
    answer: {
      type: 'list',
      items: [
        {
          label: 'Client Complexity Categorization',
          type: 'single_select',
          level: 'child',
          field: 'complexity',
          halfWidth: false,
          options: [
            {
              key: 'complexity_low',
              value: 'Low'
            },
            {
              key: 'complexity_medium',
              value: 'Medium'
            },
            {
              key: 'complexity_high',
              value: 'High'
            },
            {
              key: 'complexity_crisis',
              value: 'Crisis',
              answer: {
                type: 'text',
                rows: 3,
                name: 'complexity_crisisDesc',
                placeholder: ''
              }
            }
          ]
        }
      ]
    }
  },
  {
    label: 'Billing Information',
    field: 'momwellIntakeNetworkBillingInformation',
    type: 'info',
    answer: {
      type: 'list',
      items: [
        {
          label: 'Insurance Provider',
          type: 'single_select',
          level: 'child',
          field: 'insuranceProvider',
          halfWidth: false,
          options: [
            {
              key: 'Aetna',
              value: 'Aetna'
            },
            {
              key: 'Anthem',
              value: 'Anthem'
            },
            {
              key: 'Blueshield of CA',
              value: 'Blueshield of CA'
            },
            {
              key: 'Bluecross Blueshield of Massachusetts',
              value: 'Bluecross Blueshield of Massachusetts'
            },
            {
              key: 'Capital Bluecross PA',
              value: 'Capital Bluecross PA'
            },
            {
              key: 'Cigna',
              value: 'Cigna'
            },
            {
              key: 'Highmark Bluecross Blueshield',
              value: 'Highmark Bluecross Blueshield'
            },
            {
              key: 'Kaiser Permanente NorCal',
              value: 'Kaiser Permanente NorCal'
            },
            {
              key: 'Optum',
              value: 'Optum'
            },
            {
              key: 'Oscar Health',
              value: 'Oscar Health'
            },
            {
              key: 'Oxford',
              value: 'Oxford'
            },
            {
              key: 'United Healthcare',
              value: 'United Healthcare'
            }
          ]
        },
        {
          label: 'Member ID Number',
          type: 'text',
          placeholder: '',
          name: 'memberIDNumber',
          required: true,
          halfWidth: false
        },
        {
          label: 'Group ID Number',
          type: 'text',
          placeholder: '',
          name: 'groupIDNumber',
          required: true,
          halfWidth: false
        },
        {
          label: 'Reviewed Headway billing partnership information',
          field: 'reviewedHeadwayBillingPartnershipInformation',
          type: 'checkbox',
          name: 'reviewedHeadwayBillingPartnershipInformationConcent',
          agreeLabel: 'Yes'
        },
        {
          label: 'Reviewed Cancelation Policy',
          field: 'reviewedCancelationPolicy',
          type: 'checkbox',
          name: 'reviewedCancelationPolicyConcent',
          agreeLabel: 'Yes'
        },
        {
          label:
            'Add Payment into Headway (co-pay, co-insurance, cancellations, etc.)',
          field: 'addPaymentIntoHeadway',
          type: 'checkbox',
          name: 'addPaymentIntoHeadwayConcent',
          agreeLabel: 'Yes'
        }
      ]
    }
  },
  {
    label: 'Scheduling',
    field: 'momwellIntakeNetworkScheduling',
    type: 'info',
    answer: {
      type: 'list',
      items: [
        {
          label: 'Therapist Match',
          type: 'text',
          placeholder: '',
          name: 'therapistMatch',
          required: true,
          halfWidth: false
        },
        {
          label: 'Client Requested Therapist (if applicable)',
          type: 'text',
          placeholder: '',
          name: 'clientRequestedTherapist',
          required: false,
          halfWidth: false
        },
        {
          label: 'Therapist assigned to client in the EMR',
          field: 'therapistAssignedToClientInTheEMR',
          type: 'checkbox',
          name: 'therapistAssignedToClientInTheEMRConsent'
        },
        {
          label: 'Stage 2: Initial Assessment Booked',
          field: 'stage2InitialAssessmentBooked',
          type: 'select',
          answer: {
            type: 'single_select',
            options: [
              { key: 'yes', value: 'Yes' },
              { key: 'no', value: 'No' }
            ]
          }
        },
        {
          label: 'Reviewed Intake & Consent Forms Process',
          field: 'reviewedIntakeConsentFormsProcess',
          type: 'checkbox',
          name: 'reviewedIntakeConsentFormsProcessConsent'
        },
        {
          label: 'Booked ahead first 3 Stage 3 Ongoing Sessions',
          field: 'bookedAheadFirst3Stage3OngoingSessions',
          type: 'select',
          answer: {
            type: 'single_select',
            options: [
              { key: 'yes', value: 'Yes' },
              { key: 'no', value: 'No' }
            ]
          }
        }
      ]
    }
  },
  {
    label: 'Next Steps',
    field: 'momwellIntakeNetworkNextSteps',
    type: 'info',
    answer: {
      type: 'list',
      items: [
        {
          label: 'Confirm Headway email received',
          field: 'confirmHeadwayEmailReceived',
          type: 'checkbox',
          name: 'confirmHeadwayEmailReceivedConsent'
        },
        {
          label: 'Confirm booking confirmation email received',
          field: 'confirmBookingConfirmationEmailReceived',
          type: 'checkbox',
          name: 'confirmBookingConfirmationEmailReceivedConsent'
        },
        {
          label: 'Confirm portal account created',
          field: 'confirmPortalAccountCreated',
          type: 'checkbox',
          name: 'confirmPortalAccountCreatedConsent'
        },
        {
          label: 'Review how to join a session',
          field: 'reviewHowToJoinASession',
          type: 'checkbox',
          name: 'reviewHowToJoinASessionConsent'
        },
        {
          label:
            'Provided Instructions for booking Stage 2 Initial Assessment if not done already',
          field:
            'providedInstructionsForBookingStage2InitialAssessmentIfNotDoneAlready',
          type: 'checkbox',
          name: 'providedInstructionsForBookingStage2InitialAssessmentIfNotDoneAlreadyConsent'
        }
      ]
    }
  },
  {
    label: 'Follow Up',
    field: 'momwellIntakeNetworkFollowUp',
    type: 'info',
    answer: {
      type: 'list',
      items: [
        {
          label:
            'Stage 2 was not booked during the consultation, what was the reason?',
          type: 'text',
          placeholder: '',
          name: 'stage2WasNotBookedDuringTheConsultationWhatWasTheReason',
          required: true,
          halfWidth: false,
          rows: 3
        },
        {
          label:
            'Set reminder in Monday.com tasks to call client in one week for follow up',
          field: 'setReminderInMondayComTasksToCallClientInOneWeekForFollowUp',
          type: 'checkbox',
          name: 'setReminderInMondayComTasksToCallClientInOneWeekForFollowUpConsent'
        }
      ]
    }
  },
  {
    label: 'NOTES',
    field: 'momwellIntakeNetworkNotes',
    type: 'info',
    answer: {
      type: 'list',
      items: [
        {
          label: '',
          type: 'text',
          placeholder: '',
          name: 'momwellIntakeNetworkNotesDesc',
          required: false,
          halfWidth: false,
          rows: 5
        }
      ]
    }
  }
];

export default questionsModels;
